// Client API per il Tab Fornitori (#1 legacy parity).
// Espone i 4 endpoint backend di /api/suppliers/wp/*.

import { apiGet, apiPost, apiPostForm, apiPut } from "@/shared/api";

export interface WpCredentialsStatus {
  supplier_id: string;
  user_configured: boolean;
  password_configured: boolean;
}

export interface WpMatchItem {
  reference: string;
  supplier_order_number: string;
}

export interface WpSupplierResult {
  supplier_id: string;
  label: string;
  matches: WpMatchItem[];
  pages_scanned: number;
  error: string | null;
}

export interface WpSyncResponse {
  results: WpSupplierResult[];
  total_matches: number;
}

export interface WpApplyResponse {
  applied: number;
  skipped_no_order: number;
  skipped_already_set: number;
}

export function fetchWpCredentialsStatus(): Promise<WpCredentialsStatus[]> {
  return apiGet<WpCredentialsStatus[]>("/api/suppliers/wp/credentials");
}

export function saveWpCredentials(
  supplierId: string,
  user: string,
  password: string,
): Promise<WpCredentialsStatus> {
  return apiPut<WpCredentialsStatus>(
    `/api/suppliers/wp/${encodeURIComponent(supplierId)}/credentials`,
    { user, password },
  );
}

export function syncWpOrders(
  supplierIds: string[] = [],
  references: string[] | null = null,
): Promise<WpSyncResponse> {
  return apiPost<WpSyncResponse>("/api/suppliers/wp/sync", {
    supplier_ids: supplierIds,
    references,
  });
}

export function applyWpMatches(matches: WpMatchItem[]): Promise<WpApplyResponse> {
  return apiPost<WpApplyResponse>("/api/suppliers/wp/apply-matches", { matches });
}

// ── Tab Catalogo dimensioni fornitori (#2 legacy parity) ──────────────────

export interface SupplierCatalogColli {
  kg: number;
  l: number;
  p: number;
  a: number;
}

export interface SupplierCatalogBancale {
  kg: number;
  l: number;
  p: number;
  a: number;
  n_colli: number | null;
  colli: SupplierCatalogColli[] | null;
}

// Attributi commerciali extra (Bizzotto: import da Google Sheet, migr. 0045).
// None/assente per i fornitori dimensionali classici. I campi sono tutti
// opzionali: il backend serializza un dict JSON libero.
export interface SupplierCatalogEntryExtra {
  nome?: string;
  categoria?: string;
  prezzo?: number | null;
  ean_bizzotto?: string;
  ean_bscom?: string;
}

export interface SupplierCatalogEntry {
  supplier_id: string;
  sku: string;
  n_colli: number;
  colli: SupplierCatalogColli[];
  bancale: SupplierCatalogBancale | null;
  fonte: string;
  // Giacenza magazzino: valorizzata per AllService (sola giacenza), 0 per i
  // fornitori dimensionali (Itamoby/Ityhome/Cosma).
  giacenza: number;
  // Presente solo per Bizzotto (nome/categoria/prezzo/EAN): quando c'è, la
  // tabella entries mostra le colonne commerciali al posto di quelle colli
  // (i "colli" Bizzotto sono dimensioni prodotto con kg 0, non spedizione).
  extra?: SupplierCatalogEntryExtra | null;
}

export interface SupplierCatalogList {
  supplier_id: string;
  label: string;
  total: number;
  items: SupplierCatalogEntry[];
}

export interface SupplierCatalogRefresh {
  supplier_id: string;
  label: string;
  folder: string;
  files_seen: number;
  skus_parsed: number;
  inserted: number;
  updated: number;
  deleted: number;
  error: string | null;
}

export interface SupplierCatalogMeta {
  supplier_id: string;
  label: string;
  // True se il fornitore ha un listino Excel caricabile da UI
  // (Itamoby/Ityhome). False per Cosma (import via script) e AllService (sola
  // giacenza): il frontend nasconde il bottone "Carica Excel".
  has_listino: boolean;
}

export function listSupplierCatalogs(): Promise<SupplierCatalogMeta[]> {
  return apiGet<SupplierCatalogMeta[]>("/api/catalog/suppliers");
}

export interface SupplierCatalogQuery {
  q?: string;
  limit?: number;
  offset?: number;
}

export function fetchSupplierCatalog(
  supplierId: string,
  params: SupplierCatalogQuery = {},
): Promise<SupplierCatalogList> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.limit != null) qs.set("limit", String(params.limit));
  if (params.offset != null) qs.set("offset", String(params.offset));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return apiGet<SupplierCatalogList>(
    `/api/catalog/suppliers/${encodeURIComponent(supplierId)}${suffix}`,
  );
}

export function refreshSupplierCatalog(supplierId: string): Promise<SupplierCatalogRefresh> {
  return apiPost<SupplierCatalogRefresh>(
    `/api/catalog/suppliers/${encodeURIComponent(supplierId)}/refresh`,
    {},
  );
}

/**
 * Upload listino Excel + refresh automatico.
 *
 * Davide carica il file dalla UI invece di doverlo droppare manualmente
 * nella data_dir. Default ``replace_existing=true``: rimuove i listini
 * vecchi del fornitore per evitare confusione di versioni multiple.
 */
export function uploadSupplierCatalog(
  supplierId: string,
  file: File,
  replaceExisting: boolean = true,
): Promise<SupplierCatalogRefresh> {
  const form = new FormData();
  form.append("file", file);
  const url = `/api/catalog/suppliers/${encodeURIComponent(supplierId)}/upload?replace_existing=${replaceExisting}`;
  return apiPostForm<SupplierCatalogRefresh>(url, form);
}

// ── Import catalogo Bizzotto da Google Sheet (R4, 2026-07-03) ─────────────

export interface BizzottoImportResponse {
  imported: number;
  supplier_id: string;
}

/**
 * Importa il catalogo Bizzotto dal Google Sheet di Davide (replace-all
 * lato backend). ``sheet_url`` opzionale: il default vive server-side.
 */
export function importBizzottoCatalog(sheetUrl?: string): Promise<BizzottoImportResponse> {
  return apiPost<BizzottoImportResponse>(
    "/api/catalog/suppliers/bizzotto/import-gsheet",
    sheetUrl ? { sheet_url: sheetUrl } : {},
  );
}
